<template>
  <Container>
    <PageHeader :items="items" />

    <div class="row mb-5">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex flex-wrap align-items-center justify-content-between mb-4">
              <h4 class="card-title">Prices</h4>
              <b-button v-b-modal.price-modal @click="clearPrice" variant="outline-primary">Change Prices</b-button>
            </div>

            <b-modal size="lg" id="price-modal" title="Change Prices" title-class="font-18">
              <ValidationObserver ref="form" v-slot="{ handleSubmit }">
                <b-form id="price-form" @submit.prevent="handleSubmit(onSubmit)">
                  <b-row>
                    <b-col sm="6">
                      <b-form-group label="Start Date" label-for="start_date">
                        <ValidationProvider rules="required" v-slot="{ errors }">
                          <DatePicker
                            v-model="start_date"
                            class="w-100"
                            id="start_date"
                            placeholder="Start Date"
                            @input="onDateRangeChange"
                          ></DatePicker>

                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>

                    <b-col sm="6">
                      <b-form-group label="End Date" label-for="end_date">
                        <ValidationProvider rules="required" v-slot="{ errors }">
                          <DatePicker
                            v-model="end_date"
                            class="w-100"
                            id="end_date"
                            placeholder="End Date"
                            :disabled-date="disabledAfterStartDate"
                          ></DatePicker>

                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-form-group label="Amount" label-for="amount">
                    <ValidationProvider rules="required|positive" v-slot="{ errors }">
                      <b-form-input
                        type="number"
                        step=".01"
                        min="0"
                        v-model="amount"
                        id="amount"
                        placeholder="Enter amount"
                      ></b-form-input>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </ValidationProvider>
                  </b-form-group>

                  <b-form-group label="Lodgings" label-for="lodgings">
                    <ValidationProvider rules="required" v-slot="{ errors }">
                      <Multiselect
                        id="lodgings"
                        v-model="lodging_ids"
                        :options="lodgingsName"
                        track-by="value"
                        label="text"
                        :multiple="true"
                        :close-on-select="false"
                        deselect-label="Unselect"
                      ></Multiselect>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </ValidationProvider>
                  </b-form-group>

                  <b-form-group label="Days" label-for="days">
                    <ValidationProvider rules="required" v-slot="{ errors }">
                      <Multiselect
                        id="days"
                        v-model="days"
                        :options="daysOptions"
                        track-by="value"
                        label="text"
                        :multiple="true"
                        :close-on-select="false"
                        deselect-label="Unselect"
                      ></Multiselect>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </ValidationProvider>
                  </b-form-group>
                </b-form>
              </ValidationObserver>

              <template v-slot:modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('price-modal')">Close</b-button>
                <b-button form="price-form" type="submit" variant="primary">Save</b-button>
              </template>
            </b-modal>

            <FullCalendar :options="calendarOptions" />
          </div>
        </div>
      </div>
    </div>
  </Container>
</template>

<script>
import Container from "../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import { getLodgings, setPrices } from "@/store/api";
import FullCalendar from "@fullcalendar/vue";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import interactionPlugin from "@fullcalendar/interaction";
import bootstrapPlugin from "@fullcalendar/bootstrap";
import moment from "moment";

export default {
  page: {
    title: "Prices",
    meta: [{ name: "description", content: appConfig.description }],
  },

  components: {
    Container,
    PageHeader,
    FullCalendar,
  },

  data() {
    return {
      calendarOptions: {
        timeZone: "UTC",
        plugins: [resourceTimelinePlugin, interactionPlugin, bootstrapPlugin],
        aspectRatio: 1.5,
        headerToolbar: {
          left: "",
          center: "title",
          right: "prev,next",
        },
        columnHeader: "ddd DD/M",
        slotLabelFormat: [{ day: "numeric", weekday: "short" }],
        themeSystem: "bootstrap",
        schedulerLicenseKey: "CC-Attribution-NonCommercial-NoDerivatives",
        initialView: "resourceTimelineMonth",
        resourceAreaHeaderContent: "Accomodations",
        contentHeight: "auto",
        resourceAreaWidth: "250px",
        resources: [],
        events: [],
      },

      start_date: "",
      end_date: "",
      amount: "",
      days: [
        { text: "Sun", value: 0 },
        { text: "Mon", value: 1 },
        { text: "Tue", value: 2 },
        { text: "Wed", value: 3 },
        { text: "Thu", value: 4 },
        { text: "Fri", value: 5 },
        { text: "Sat", value: 6 },
      ],
      lodging_ids: [],
      lodgings: [],
      daysOptions: [
        { text: "Sun", value: 0 },
        { text: "Mon", value: 1 },
        { text: "Tue", value: 2 },
        { text: "Wed", value: 3 },
        { text: "Thu", value: 4 },
        { text: "Fri", value: 5 },
        { text: "Sat", value: 6 },
      ],
      items: [
        {
          text: "Home",
          to: "/",
        },
        {
          text: "Settings",
          to: "/settings",
        },
        {
          text: "Prices & Rules",
          active: true,
        },
      ],
    };
  },

  created() {
    getLodgings({ price: true, date: moment(Date.today).format("YYYY-MM-DD") }).then((response) => {
      this.lodgings = response;
      for (let lodging of this.lodgings) {
        this.calendarOptions.resources.push({ id: lodging.id, title: lodging.attributes.name });
        for (let price of lodging.attributes.prices.data) {
          this.calendarOptions.events.push({
            id: price.id,
            resourceId: lodging.id,
            title: price.attributes.amount,
            start: price.attributes.date,
            end: moment(price.attributes.date).add(1, "day").format("YYYY-MM-DD"),
            backgroundColor: "white",
            textColor: "black",
          });
        }
      }
    });
  },

  computed: {
    lodgingsName() {
      return this.lodgings.map((lodging) => ({ value: lodging.id, text: lodging.attributes.name }));
    },
  },

  methods: {
    clearPrice() {
      this.start_date = "";
      this.end_date = "";
      this.amount = "";
      this.days = this.daysOptions;
      this.lodging_ids = [];
    },

    disabledAfterStartDate(date) {
      return date < this.start_date;
    },

    onDateRangeChange() {
      if (this.start_date > this.end_date && this.end_date != "") {
        this.end_date = this.start_date;
      }
    },

    onSubmit() {
      this.$bvModal.hide("price-modal");
      this.days = this.days.map((day) => day.value);
      this.lodging_ids = this.lodging_ids.map((lodging_id) => lodging_id.value);
      setPrices(
        moment(this.start_date).format("YYYY-MM-DD"),
        moment(this.end_date).format("YYYY-MM-DD"),
        this.amount,
        this.days,
        this.lodging_ids
      ).then((response) => {
        console.log(response);
      });
    },
  },
};
</script>

<style>
.fc-event-start {
  margin-left: 0% !important;
}

.fc-event-end {
  margin-right: 0% !important;
}

.fc-event {
  cursor: default !important;
}
</style>
